import { Delete, Edit, RemoveRedEyeSharp } from "@mui/icons-material";
import { Button, TextField } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/Pagination";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "var(--text-main)",
    "& fieldset": { borderColor: "var(--border)" },
    "&:hover fieldset": { borderColor: "var(--primary)" },
    "&.Mui-focused fieldset": { borderColor: "var(--primary)" },
  },
  "& .MuiInputLabel-root": { color: "var(--text-muted)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--primary)" },
};

const MaterialEntryList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery(
    ["get_material_entries", currentPage, search],
    () =>
      axiosInstance.post(API_URLS.get_material_entries, {
        search: search?.trim(),
        page: currentPage,
        count: 10,
      }),
    { keepPreviousData: true }
  );

  const allData = data?.data?.response || {};
  const rows = allData?.data || [];

  const handleView = async (entry) => {
    try {
      const res = await axiosInstance.post(API_URLS.get_material_entry_detail, { id: entry.id });
      const fullEntry = res.data?.response;
      navigate("/add-material-entry", { state: { entry: fullEntry } });
    } catch (e) {
      Swal.fire("Error", "Failed to load entry details", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This material entry will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await axiosInstance.post(API_URLS.delete_material_entry, { id });
      if (!res.data.success) {
        Swal.fire("Error!", res.data.msg, "error");
        return;
      }
      Swal.fire("Deleted!", "Material entry deleted successfully.", "success");
      queryClient.invalidateQueries("get_material_entries");
    } catch {
      Swal.fire("Error!", "Failed to delete entry", "error");
    }
  };

  const tableHead = [
    "S.No.", "Project Name", "Client", "Location", "Date", "Site Engineer", "Work Type", "Status", "Created At", "Action",
  ];

  const tableRow = rows.map((entry, index) => [
    index + 1 + (currentPage - 1) * 10,
    entry.crm_project_name || "--",
    entry.crm_client || "--",
    entry.crm_location || "--",
    entry.crm_date ? moment(entry.crm_date).format("DD-MM-YYYY") : "--",
    entry.crm_site_engineer || "--",
    entry.crm_work_type || "--",
    <span
      className="px-2 py-1 rounded-full text-xs font-semibold"
      style={{
        background: entry.crm_status === "Submitted" ? "rgba(76,203,113,0.15)" : "rgba(200,144,64,0.15)",
        color: entry.crm_status === "Submitted" ? "#4ecb71" : "var(--primary)",
      }}
    >
      {entry.crm_status || "Draft"}
    </span>,
    entry.crm_created_at ? moment.utc(entry.crm_created_at).format("DD-MM-YYYY HH:mm") : "--",
    <div className="flex gap-2">
      <RemoveRedEyeSharp style={{ color: "var(--primary)", cursor: "pointer" }} onClick={() => handleView(entry)} />
      <Edit style={{ color: "#4ecb71", cursor: "pointer" }} onClick={() => handleView(entry)} />
      <Delete style={{ color: "#e05555", cursor: "pointer" }} onClick={() => handleDelete(entry.id)} />
    </div>,
  ]);

  return (
    <div>
      <div className="flex justify-between mb-4 flex-wrap gap-3">
        <p className="font-bold text-xl" style={{ color: "var(--text-main)" }}>Material Entries</p>
        <Button
          variant="contained"
          onClick={() => navigate("/add-material-entry")}
          sx={{
            backgroundColor: "var(--primary)",
            color: "#000",
            fontWeight: 600,
            "&:hover": { backgroundColor: "var(--primary-hover)" },
          }}
        >
          + Add Material Entry
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <TextField
          type="search"
          placeholder="Search by project, client or location"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          sx={fieldSx}
        />
      </div>

      <CustomTable tablehead={tableHead} tablerow={tableRow} isLoading={isLoading} />
      <CustomToPagination page={currentPage} setPage={setCurrentPage} data={allData} />
    </div>
  );
};

export default MaterialEntryList;
